import React, { forwardRef, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import _, { find } from 'lodash';
import { FieldTypes, parseSelectOptions } from 'utils/formUtils';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import{ Form } from 'antd';

const ApiiroMappingForm = forwardRef(function ApiiroMappingForm({disableProjectFields = false, ...props}, ref) {
  const editModeRef = useRef(!_.isEmpty(props.record));

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [repositoryList, setRepositoryList] = useState(() => {
    if(props.record && props.record.repositoryKey){
        const { applicationKey, repositoryKey, repositoryName} = props.record;
        const repoArray = repositoryKey.split(',') || [];
        const repoNameArray = repositoryName.split(',') || [];
        return {
            [applicationKey]: repoArray.map((id, index) => ({id, name: repoNameArray[index]}))
        }
    }
    return {}
  });
  const [projects, setProjects] = useState(() => {
    if (props.record) {
      const { applicationKey, applicationName } = props.record;
      return [{ id: applicationKey, name: applicationName }]
    }
    return [];
  });

  const [applicationKey, setApplicationKey] = useState(() => {
    if(props.record){
        const { applicationKey } = props.record;
        return applicationKey
    };
    return ''
  });

  useEffect(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
    promise.then((response) => {
      const { projects } = response;
      setProjects(projects);
    }).catch(() => {
      message.error('Problem fetching projects');
    });

    return cancel;
  }, [props.loginConfigId, props.toolName]);

  const fetchRepositories = useCallback((application) => {
    if((editModeRef.current && application) || (application && !repositoryList[application])){
      editModeRef.current = false;
      setLoading(true)
      const {promise, cancel} = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, 
        { login_id: props.loginConfigId, organisation: application});

        promise.then(response=>{
          const { projects } = response;
          setRepositoryList((repositoryList) =>({...repositoryList,[application]: projects}));
        }).catch(()=>{
        message.error('Problem fetching repositories')
        }).finally(() => setLoading(false))

        return cancel;
    }
  }, [repositoryList, props.loginConfigId, props.toolName]);

  useEffect(()=> fetchRepositories(applicationKey), [applicationKey, fetchRepositories]);

  const getExtraFieldsData = useCallback((config) => {
    const { applicationKey, repositoryKey } = config;
    const applicationName = find(projects, (obj) => obj.id === applicationKey)?.name;

    const data = repositoryList[applicationKey]?.reduce((acc,cv) =>{
      const { id, name } = cv;
      if(repositoryKey?.includes(id)){
        acc.id.push(id);
        acc.name.push(name);
      }
      return acc;
    },{id:[],name:[]})

    const appendApplicationName =  data.name.map(n => `${applicationName}/${n}`).join();
    return ({
      applicationKey,
      applicationName,
      repositoryKey:  _.isEmpty(data?.id) ? null : data.id.join(),
      repositoryName: appendApplicationName,
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }, [projects, repositoryList]);

  const onApplicationChange = useCallback((value, field) => {
    setApplicationKey(value);
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    let updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
     repositoryKey: undefined
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const extraFields = useMemo(() => ([
    {
      name: 'applicationKey',
      colSpan: '12',
      type: FieldTypes.SELECT,
      label: 'Application Name',
      options: parseSelectOptions(projects),
      disabled: disableProjectFields, 
      required: true,
      props: {
        autoFocus: true,
        onChange: onApplicationChange
      }
    },  
    {
      name: 'repositoryKey',
      colSpan: 12,
      type: FieldTypes.MULTI_SELECT,
      label: 'Repository',
      required: true,
      disabled: disableProjectFields,
      props: {
        getFormListOptions: (fieldKey) => {
          const configs = form.getFieldValue("configurations")
          const applicationKey = configs[fieldKey]?.applicationKey
          return applicationKey ? parseSelectOptions(repositoryList[applicationKey]): []
        },
        loading
      },
    },
  ]), [projects, form, repositoryList, onApplicationChange, loading, disableProjectFields]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { applicationKey, repositoryKey } = config;
    return ({
        applicationKey,
        repositoryKey: repositoryKey?.split(',')
    });
}, []);

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      projects={projects}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      {...props}
    />
  )
})

export default ApiiroMappingForm;
